import { ViewMode } from '@efdigital/ll-addon-codemirror';
import type { CalendarItem, PicklistItem } from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import {
  EditRecord,
  initialState,
} from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { MarkdownEditorWithImagePicker } from '@efdigital/ll-admin-v2/components/markdown-editor-with-image-picker';
import { NoEmoji } from '@efdigital/ll-admin-v2/lib/markdown-toolbar';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { set } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import { InputDateOnly, InputDateTime } from '../../input-date.gts';
import ImagePickerBig from '../../pickers/image-picker-big.gts';
import { PicklistItemPicker } from '../../pickers/picklist-item-picker.gts';

function isAllDay(calendarItem: CalendarItem) {
  return calendarItem.dateType === 'date';
}

function isChecked(value: string, checkedValue: string) {
  return value === checkedValue;
}

export class EditCalendarItem extends Component<
  EditRecordSignature<
    CalendarItem,
    {
      showEventFields: boolean;
      showContentFields: boolean;
    }
  >
> {
  @service declare store: Store;

  setValue = (property: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(this.args.item, property, target.value);
  };

  setChecked = (property: string, event: Event) => {
    const target = event.target as HTMLInputElement;
    set(this.args.item, property, target.checked);
  };

  setMarkdown = (markdown: string) => {
    set(this.args.item, 'markdown', markdown);
  };

  setDateValue = (property: string, value: string) => {
    set(this.args.item, property, value);
  };

  setIcon = (icon: string) => {
    set(this.args.item, 'icon', icon);
  };

  setEventType = (eventType: PicklistItem | null) => {
    if (eventType) {
      this.args.item.eventType = eventType.key;
    }
  };

  get editLabel() {
    if (this.args.showEventFields && this.args.showContentFields) {
      return 'Edit Event';
    } else if (this.args.showEventFields) {
      return 'Edit Event Details';
    } else if (this.args.showContentFields) {
      return 'Edit Content';
    }
    return 'Edit Event';
  }

  <template>
    <EditRecord
      @editLabel={{this.editLabel}}
      @item={{@item}}
      @context={{@context}}
      @initialState={{initialState @item}}
      @maxDepth={{1}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        {{#if @showEventFields}}
          <fieldset disabled={{isReadOnly}} class="mb-4">
            <legend class="gud-eyebrow">Event</legend>

            <PicklistItemPicker
              @group="calendar-event-type"
              @label="Event Type"
              @name="eventType"
              @selected={{@item.eventType}}
              @readonly={{isReadOnly}}
              @onSelect={{this.setEventType}}
              class="mb-3 required"
            />

            <div class="mb-3">
              <label
                for="{{uniqueId}}-title"
                class="form-label required"
              >Title</label>
              <input
                id="{{uniqueId}}-title"
                name="title"
                type="text"
                readonly={{isReadOnly}}
                value={{@item.title}}
                class="form-control"
                placeholder="Event name"
                required
                {{debounce (fn this.setValue "title")}}
              />
            </div>
          </fieldset>

          <fieldset disabled={{isReadOnly}} class="mb-4">
            <legend class="gud-eyebrow">When and Where</legend>

            <div class="mb-3">
              <label class="form-label visually-hidden">Duration</label>
              <div class="d-flex flex-row gap-4 justify-content-stretch">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="dateType"
                    id="{{uniqueId}}-dateType-dateTime"
                    value="date-time"
                    checked={{isChecked @item.dateType "date-time"}}
                    {{on "change" (fn this.setValue "dateType")}}
                  />
                  <label
                    class="form-check-label"
                    for="{{uniqueId}}-dateType-dateTime"
                  >
                    Specific time
                  </label>
                </div>
                <div class="form-check me-auto">
                  <input
                    class="form-check-input"
                    type="radio"
                    name="dateType"
                    id="{{uniqueId}}-dateType-date"
                    value="date"
                    checked={{isChecked @item.dateType "date"}}
                    {{on "change" (fn this.setValue "dateType")}}
                  />
                  <label
                    class="form-check-label"
                    for="{{uniqueId}}-dateType-date"
                  >
                    All day
                  </label>
                </div>

                <div class="form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="{{uniqueId}}-cancelled"
                    readonly={{isReadOnly}}
                    checked={{@item.cancelled}}
                    aria-checked={{@item.cancelled}}
                    value="true"
                    {{on "change" (fn this.setChecked "cancelled")}}
                  />
                  <label
                    class="form-check-label"
                    for="{{uniqueId}}-cancelled"
                  >Canceled</label>
                </div>
              </div>
            </div>

            {{#if (isAllDay @item)}}
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="{{uniqueId}}-starts-date"
                      class="form-label required"
                    >Starts</label>
                    <InputDateOnly
                      id="{{uniqueId}}-starts-date"
                      name="starts-date"
                      class="form-control"
                      readonly={{isReadOnly}}
                      required
                      @value={{@item.starts}}
                      @onChange={{fn this.setDateValue "starts"}}
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="{{uniqueId}}-ends-date"
                      class="form-label"
                    >Ends</label>
                    <InputDateOnly
                      id="{{uniqueId}}-ends-date"
                      name="ends-date"
                      class="form-control"
                      readonly={{isReadOnly}}
                      @value={{@item.ends}}
                      @onChange={{fn this.setDateValue "ends"}}
                    />
                  </div>
                </div>
              </div>
            {{else}}
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="{{uniqueId}}-starts-datetime"
                      class="form-label required"
                    >Starts</label>
                    <InputDateTime
                      id="{{uniqueId}}-starts-datetime"
                      name="starts-datetime"
                      class="form-control"
                      required
                      readonly={{isReadOnly}}
                      @value={{@item.starts}}
                      @onChange={{fn this.setDateValue "starts"}}
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label
                      for="{{uniqueId}}-ends-datetime"
                      class="form-label"
                    >Ends</label>
                    <InputDateTime
                      id="{{uniqueId}}-ends-datetime"
                      name="ends-datetime"
                      class="form-control"
                      readonly={{isReadOnly}}
                      @value={{@item.ends}}
                      @onChange={{fn this.setDateValue "ends"}}
                    />
                  </div>
                </div>
              </div>
            {{/if}}

            <div class="mb-3">
              <label
                for="{{uniqueId}}-location"
                class="form-label"
              >Location</label>
              <input
                id="{{uniqueId}}-location"
                name="location"
                type="text"
                readonly={{isReadOnly}}
                value={{@item.location}}
                class="form-control"
                placeholder="Location"
                {{debounce (fn this.setValue "location")}}
              />
            </div>

            <div class="mb-3">
              <label for="{{uniqueId}}-external" class="form-label">Meeting Link</label>
              <input
                id="{{uniqueId}}-external"
                name="external"
                type="url"
                readonly={{isReadOnly}}
                value={{@item.external}}
                class="form-control"
                placeholder="e.g. Teams meeting link"
                {{debounce (fn this.setValue "external")}}
              />
            </div>
          </fieldset>
        {{/if}}

        {{#if @showContentFields}}
          <fieldset disabled={{isReadOnly}} class="mb-4">
            <legend class="gud-eyebrow">Content</legend>

            <div class="mb-3">
              <label for="abstract" class="form-label">Body</label>
              {{#if isReadOnly}}
                <textarea
                  class="form-control font-monospace form-control-small lh-sm"
                  id="abstract"
                  rows="15"
                  readonly
                >{{@item.markdown}}</textarea>
              {{else}}
                <MarkdownEditorWithImagePicker
                  @markdown={{@item.markdown}}
                  @toolbar={{NoEmoji}}
                  @mode={{ViewMode.EditorOnly}}
                  @onUpdate={{this.setMarkdown}}
                  class="ll-editor-markdown"
                  @requiredSize="email-image"
                >
                  <:preview as |markdown|>
                    <pre>{{markdown}}</pre>
                  </:preview>
                </MarkdownEditorWithImagePicker>
              {{/if}}
            </div>

            <div class="mb-3">
              <label for="{{uniqueId}}-icon" class="form-label">Image</label>

              <ImagePickerBig
                @value={{@item.icon}}
                @onChange={{this.setIcon}}
                @sizeHint="email-image"
                @defaultTitle={{@item.title}}
                @readonly={{isReadOnly}}
                @required={{false}}
                @id="{{uniqueId}}-icon"
                @placeholder="https://"
              />
            </div>

          </fieldset>
        {{/if}}
      </:form>
    </EditRecord>
  </template>
}
